import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

export type ScheduleOption = { value: string; label: string; description?: string; keywords?: string[] };

type Props = {
  id: string;
  value: string;
  options: ScheduleOption[];
  onChange: (value: string) => void;
  searchLabel: string;
  icon?: ReactNode;
  customOption?: (search: string) => ScheduleOption | undefined;
};

export function ScheduleSelect({ id, value, options, onChange, searchLabel, icon, customOption }: Props) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = options.find((option) => option.value === value);
  const custom = customOption?.(search);
  const choices = custom && !options.some((option) => option.value === custom.value)
    ? [custom, ...options] : options;

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button id={id} type="button" variant="outline" role="combobox" aria-expanded={open}
          aria-controls={listId} aria-haspopup="listbox"
          className="h-10 w-full min-w-0 justify-between gap-2 bg-background px-3 font-normal hover:bg-muted hover:text-foreground">
          {icon}
          <span className="min-w-0 flex-1 truncate text-left tabular-nums">{selected?.label ?? value}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[max(12rem,var(--radix-popover-trigger-width))] max-w-[calc(100vw-2rem)] p-0">
        <Command defaultValue={value} filter={(optionValue, query, keywords) => {
          const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/_/g, ' ');
          const haystack = normalize([optionValue, ...(keywords ?? [])].join(' '));
          return normalize(query).trim().split(/\s+/).every((part) => haystack.includes(part)) ? 1 : 0;
        }}>
          <CommandInput value={search} onValueChange={setSearch} placeholder={searchLabel} aria-label={searchLabel} />
          <CommandList id={listId} className="max-h-56 overscroll-contain">
            <CommandEmpty>{t('publishQueues.form.noResults')}</CommandEmpty>
            <CommandGroup>
              {choices.map((option) => (
                <CommandItem key={option.value} value={option.value}
                  keywords={[option.label, option.description ?? '', ...(option.keywords ?? [])]}
                  className="cursor-pointer gap-2 rounded-md px-2.5 py-2.5 data-[selected=true]:bg-primary/10 data-[selected=true]:text-foreground"
                  onSelect={() => { onChange(option.value); setOpen(false); setSearch(''); }}>
                  <span className="min-w-0 flex-1">
                    <span className="block tabular-nums">{option.label}</span>
                    {option.description && <span className="block text-xs text-muted-foreground">{option.description}</span>}
                  </span>
                  <Check aria-hidden="true" className={`h-4 w-4 shrink-0 text-primary ${option.value === value ? '' : 'invisible'}`} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
